import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Youtube, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useSEO } from '../hooks/useSEO';
export function ContactPage() {
  useSEO({
    title: 'Contact Us — Robeta North America',
    description: 'Get in touch with Robeta North America. Reach out for sales inquiries, dealer partnerships, owner support, or general questions.'
  });
  return <main className="bg-white min-h-screen pt-32 md:pt-40" data-id="element-428">
      {/* 1. HERO SECTION */}
      <section className="container mx-auto px-6 md:px-12 mb-20 md:mb-32" data-id="element-429">
        <Breadcrumbs items={[{
        label: 'Home',
        href: '/'
      }, {
        label: 'Contact'
      }]} className="mb-8" data-id="element-430" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl" data-id="element-431">
          <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-432">
            Get in Touch
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial leading-tight mb-6" data-id="element-433">
            Contact Us
          </h1>
          <p className="text-lg md:text-xl font-light text-robeta-black/60 max-w-2xl leading-relaxed" data-id="element-434">
            We're here to help. Reach out to Robeta North America for sales,
            support, or general inquiries.
          </p>
        </motion.div>
      </section>

      {/* 2. CONTACT DETAILS & FORM */}
      <section className="container mx-auto px-6 md:px-12 mb-24" data-id="element-435">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24" data-id="element-436">
          {/* Left: Contact Info */}
          <div className="w-full lg:w-1/3 space-y-12" data-id="element-437">
            <div data-id="element-438">
              <h3 className="text-lg font-bold text-robeta-black uppercase tracking-wide mb-6" data-id="element-439">
                Contact Information
              </h3>
              <div className="space-y-6" data-id="element-440">
                <div className="flex items-start gap-4" data-id="element-441">
                  <Phone className="w-5 h-5 text-robeta-copper mt-1" data-id="element-442" />
                  <div data-id="element-443">
                    <span className="block text-xs font-bold uppercase tracking-widest text-robeta-black/40 mb-1" data-id="element-444">
                      Phone
                    </span>
                    <a href="tel:+18005550199" className="text-robeta-black hover:text-robeta-copper transition-colors" data-id="element-445">
                      +1 (800) 555-0199
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-446">
                  <Mail className="w-5 h-5 text-robeta-copper mt-1" data-id="element-447" />
                  <div data-id="element-448">
                    <span className="block text-xs font-bold uppercase tracking-widest text-robeta-black/40 mb-1" data-id="element-449">
                      Email
                    </span>
                    <a href="mailto:hello@robeta.ca" className="text-robeta-black hover:text-robeta-copper transition-colors" data-id="element-450">
                      hello@robeta.ca
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-451">
                  <MapPin className="w-5 h-5 text-robeta-copper mt-1" data-id="element-452" />
                  <div data-id="element-453">
                    <span className="block text-xs font-bold uppercase tracking-widest text-robeta-black/40 mb-1" data-id="element-454">
                      Headquarters
                    </span>
                    <p className="text-robeta-black leading-relaxed" data-id="element-455">
                      123 Adventure Way
                      <br data-id="element-456" />
                      Squamish, BC V8B 0A1
                      <br data-id="element-457" />
                      Canada
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-458">
                  <Clock className="w-5 h-5 text-robeta-copper mt-1" data-id="element-459" />
                  <div data-id="element-460">
                    <span className="block text-xs font-bold uppercase tracking-widest text-robeta-black/40 mb-1" data-id="element-461">
                      Business Hours
                    </span>
                    <p className="text-robeta-black leading-relaxed" data-id="element-462">
                      Mon - Fri: 9am - 5pm PST
                      <br data-id="element-463" />
                      Sat - Sun: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-12 border-t border-robeta-black/10" data-id="element-464">
              <h3 className="text-lg font-bold text-robeta-black uppercase tracking-wide mb-6" data-id="element-465">
                Legal
              </h3>
              <p className="text-sm text-robeta-black/60 font-light leading-relaxed" data-id="element-466">
                Robeta Canada Ltd.
                <br data-id="element-467" />
                BN: 123456789 RT0001
                <br data-id="element-468" />
                Registered in British Columbia
              </p>
            </div>

            <div className="pt-12 border-t border-robeta-black/10" data-id="element-469">
              <h3 className="text-lg font-bold text-robeta-black uppercase tracking-wide mb-6" data-id="element-470">
                Follow Us
              </h3>
              <div className="flex gap-4" data-id="element-471">
                {[Instagram, Facebook, Youtube].map((Icon, i) => <a key={i} href="#" className="w-10 h-10 border border-robeta-black/10 flex items-center justify-center hover:border-robeta-copper hover:text-robeta-copper transition-all" data-id="element-472">
                    <Icon className="w-4 h-4" data-id="element-473" />
                  </a>)}
              </div>
            </div>
          </div>

          {/* Right: Inquiry Form */}
          <div className="w-full lg:w-2/3 bg-robeta-mist/10 p-8 md:p-12" data-id="element-474">
            <h3 className="text-2xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-475">
              Send an Inquiry
            </h3>
            <form className="space-y-6" onSubmit={e => e.preventDefault()} data-id="element-476">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-477">
                <div data-id="element-478">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-479">
                    Name
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm" data-id="element-480" />
                </div>
                <div data-id="element-481">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-482">
                    Email
                  </label>
                  <input type="email" className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm" data-id="element-483" />
                </div>
                <div data-id="element-484">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-485">
                    Phone (Optional)
                  </label>
                  <input type="tel" className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm" data-id="element-486" />
                </div>
                <div data-id="element-487">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-488">
                    Inquiry Type
                  </label>
                  <select className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm appearance-none" data-id="element-489">
                    <option data-id="element-490">General Inquiry</option>
                    <option data-id="element-491">Sales</option>
                    <option data-id="element-492">Dealer Opportunities</option>
                    <option data-id="element-493">Owner Support</option>
                  </select>
                </div>
              </div>
              <div data-id="element-494">
                <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-495">
                  Message
                </label>
                <textarea rows={6} className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm resize-none" data-id="element-496"></textarea>
              </div>
              <button className="w-full py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors" data-id="element-497">
                Submit Inquiry
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* 3. NEWSLETTER */}
      <section className="py-24 bg-robeta-black text-white" data-id="element-498">
        <div className="container mx-auto px-6 md:px-12 text-center" data-id="element-499">
          <h2 className="text-3xl font-bold uppercase tracking-editorial mb-4" data-id="element-500">
            Stay Connected
          </h2>
          <p className="text-white/60 font-light mb-8 max-w-md mx-auto" data-id="element-501">
            Join our newsletter for the latest news, events, and stories from
            the road.
          </p>
          <form className="flex flex-col sm:flex-row max-w-md mx-auto gap-4" onSubmit={e => e.preventDefault()} data-id="element-502">
            <input type="email" placeholder="Enter your email" className="flex-grow px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-white/40 focus:border-robeta-copper focus:outline-none transition-colors text-sm" data-id="element-503" />
            <button className="px-8 py-3 bg-white text-robeta-black font-bold uppercase tracking-widest hover:bg-robeta-copper hover:text-white transition-colors text-sm" data-id="element-504">
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </main>;
}